import React, { useState, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  IconButton,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Stack,
  Chip,
  CircularProgress,
  OutlinedInput,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import CustomPagination from 'src/components/CustomPagination';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financialSimulationService } from 'src/services/financial-simulation.service';
import { useDebounce } from 'src/hooks/useDebounce';
import { useAuth } from 'src/context/auth/AuthContext';
import { toast } from 'react-toastify';

export function ScenarioLibrary() {
  const navigate = useNavigate();
  const { hasPermission, getBaseRoute } = useAuth();
  const editScenarioRoute = getBaseRoute('/edit-scenario');
  const addScenarioRoute = getBaseRoute('/add-scenario');

  const canCreate = hasPermission('whammies.create');
  const canEdit = hasPermission('whammies.edit');
  const canDelete = hasPermission('whammies.delete');
  const canAssign = hasPermission('whammies.assign');

  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [scenarioToDelete, setScenarioToDelete] = useState<{ id: string; title: string } | null>(null);

  const queryClient = useQueryClient();
  const debouncedSearch = useDebounce(searchText, 300);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => financialSimulationService.deleteScenario(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['whammies-metrics'] });
      toast.success('Scenario deleted successfully');
      setOpen(false);
      setScenarioToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete scenario');
    },
  });

  const queryParams = useMemo(
    () => ({
      page,
      limit: rowsPerPage,
      search: debouncedSearch,
    }),
    [page, rowsPerPage, debouncedSearch]
  );

  const {
    data: scenariosResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['scenarios', queryParams],
    queryFn: () => financialSimulationService.getScenarios(queryParams),
  });

  const scenariosList = scenariosResponse?.data ?? [];

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleEditClick = useCallback(
    (id: string) => {
      navigate(`${editScenarioRoute}/${id}`);
    },
    [navigate, editScenarioRoute]
  );

  const handleDeleteClick = useCallback((id: string, title: string) => {
    setScenarioToDelete({ id, title });
    setOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (scenarioToDelete) {
      deleteMutation.mutate(scenarioToDelete.id);
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Search & Action Bar */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems="center">
          <OutlinedInput
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
            placeholder="Search scenarios by title or description..."
            startAdornment={
              <InputAdornment position="start">
                <Icon icon="solar:magnifer-linear" width={16} style={{ color: '#94A3B8' }} />
              </InputAdornment>
            }
            sx={{
              height: 38,
              fontSize: '0.875rem',
              borderRadius: 1.75,
              width: { xs: '100%', sm: 340 },
              bgcolor: 'grey.100',
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: 'grey.200' },
            }}
          />

          {canCreate && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
              onClick={() => navigate(addScenarioRoute)}
              sx={{
                height: 38,
                borderRadius: 1.5,
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                px: 2,
              }}
            >
              + Create New Scenario
            </Button>
          )}
        </Stack>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table sx={{ minWidth: 700 }}>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 1.75, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Scenario Title</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 1.75, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Description / Context</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 1.75, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Penalty Expense</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', py: 1.75, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} thickness={4} />
                  <Typography variant="caption" sx={{ mt: 1.25, color: 'text.secondary', fontWeight: 600, display: 'block', fontSize: '0.8125rem' }}>
                    Loading scenario library...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : scenariosList.length > 0 ? (
              scenariosList.map((row) => {
                const { id, title, description, amount } = row;
                const penaltyAmount = Number(amount) || 0;

                return (
                  <TableRow
                    key={id}
                    hover
                    sx={{
                      transition: 'background-color 0.15s ease',
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.75,
                            bgcolor: 'rgba(217, 119, 6, 0.12)',
                            color: '#D97706',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon icon="solar:danger-triangle-bold-duotone" width={20} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.875rem' }}>
                          {title}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ py: 2, maxWidth: 380 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.8125rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {description || 'No description provided'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        icon={<Icon icon="solar:danger-triangle-bold-duotone" width={14} color="#DC2626" />}
                        label={`$${penaltyAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        size="small"
                        sx={{
                          height: 26,
                          fontSize: '0.8125rem',
                          fontWeight: 800,
                          borderRadius: 1.25,
                          bgcolor: 'rgba(239, 68, 68, 0.12)',
                          color: '#DC2626',
                          border: '1px solid',
                          borderColor: 'rgba(239, 68, 68, 0.25)',
                        }}
                      />
                    </TableCell>

                    <TableCell align="right" sx={{ py: 2 }}>
                      <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                        {canAssign && (
                          <Tooltip title="Assign Challenge to Scholar">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`${assignChallengeRoute}?scenarioId=${id}`)}
                              sx={{
                                color: 'warning.dark',
                                bgcolor: 'rgba(237, 180, 46, 0.12)',
                                '&:hover': { bgcolor: 'rgba(237, 180, 46, 0.25)' },
                              }}
                            >
                              <Icon icon="solar:play-circle-bold" width={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canEdit && (
                          <Tooltip title="Edit Scenario">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`${editScenarioRoute}/${id}`)}
                              sx={{
                                color: 'text.secondary',
                                bgcolor: 'grey.100',
                                '&:hover': { bgcolor: 'grey.200', color: 'text.primary' },
                              }}
                            >
                              <Icon icon="solar:pen-2-bold" width={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete Scenario">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(id, title)}
                              sx={{
                                color: 'error.main',
                                bgcolor: 'rgba(239, 68, 68, 0.08)',
                                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.16)' },
                              }}
                            >
                              <Icon icon="solar:trash-bin-trash-bold" width={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                  <Box sx={{ maxWidth: 360, mx: 'auto', textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        color: 'text.secondary',
                      }}
                    >
                      <Icon icon="solar:library-bold-duotone" width={32} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                      No Scenarios Found
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                      {searchText ? `No scenarios match "${searchText}".` : 'Get started by creating your first Whammy template.'}
                    </Typography>
                    {canCreate && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
                        onClick={() => navigate(addScenarioRoute)}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 700,
                          textTransform: 'none',
                          px: 2,
                        }}
                      >
                        Create Scenario
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {scenariosResponse && scenariosResponse.total > 0 && (
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <CustomPagination
            count={scenariosResponse.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Box>
      )}

      {/* Confirmation Delete Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1.5,
            maxWidth: 420,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'rgba(239, 68, 68, 0.12)',
                color: '#DC2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon icon="solar:trash-bin-trash-bold" width={22} />
            </Box>
            <span>Delete Scenario</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete <strong>&ldquo;{scenarioToDelete?.title}&rdquo;</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setOpen(false)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isPending}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Scenario'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
